import * as dateFns from 'date-fns'

import { Instrument } from '../../types/shared'
import { DateRangeType, TIMEFRAME } from '../constants/common'
import { formatDateToStringUS } from '../utils/helpers'

export const CUSTOM_DATE_RANGE = 'custom'

export enum CustomDateRangePrefixEnum {
  Range = 'range',
  Prev = 'prev',
}

export const DATE_RANGE_PREFIX_DELMITER = '_'
const CUSTOM_DATE_RANGE_DELIMITER = 'x'
const CUSTOM_PREV_RANGE_DELIMITER = '-'

export function parseCustomDateRangeUrlParam({ dateRange, anchorDate }: { dateRange: string; anchorDate: Date }) {
  const [dateRangePrefix, dateRangeValue] = dateRange.split(DATE_RANGE_PREFIX_DELMITER)

  if (dateRangePrefix === CustomDateRangePrefixEnum.Range) {
    const [dateFromString, dateToString] = dateRangeValue.replace(/-/g, '/').split(CUSTOM_DATE_RANGE_DELIMITER)
    const dateFrom = dateFromString ? new Date(dateFromString) : null
    const dateTo = dateToString ? new Date(dateToString) : null
    return {
      start: dateFrom && !Number.isNaN(dateFrom.getTime()) ? dateFrom : null,
      end: dateTo && !Number.isNaN(dateTo.getTime()) ? dateFns.endOfDay(dateTo) : null,
    }
  }

  if (dateRangePrefix === CustomDateRangePrefixEnum.Prev) {
    const [years, months, days] = dateRangeValue.split(CUSTOM_PREV_RANGE_DELIMITER).map(Number)

    if ([years, months, days].some(Number.isNaN)) {
      return { start: null, end: null }
    }

    return {
      start: dateFns.startOfDay(dateFns.sub(anchorDate, { years, months, days })),
      end: anchorDate,
    }
  }

  return { start: null, end: anchorDate }
}

export function getCustomStartEndDateRangeUrlParam({ start, end }: { start: Date | null; end: Date | null }) {
  const startString = start ? formatDateToStringUS(start) : ''
  const endString = end ? formatDateToStringUS(end) : ''

  const stringParts = [
    CustomDateRangePrefixEnum.Range,
    DATE_RANGE_PREFIX_DELMITER,
    startString,
    CUSTOM_DATE_RANGE_DELIMITER,
    endString,
  ]

  return stringParts.join('').replace(/\//g, '-')
}

export function getPrevPeriodDateRangeUrlParam({
  years,
  months,
  days,
}: {
  years: number
  months: number
  days: number
}) {
  const stringParts = [
    CustomDateRangePrefixEnum.Prev,
    DATE_RANGE_PREFIX_DELMITER,
    years,
    CUSTOM_PREV_RANGE_DELIMITER,
    months,
    CUSTOM_PREV_RANGE_DELIMITER,
    days,
  ]

  return stringParts.join('')
}

export function getEnumDateRangeUrlParam(dateRangeType: DateRangeType) {
  return dateRangeType
}

const DAYS_IN_YEAR = 365

export function getTimeframeFromDateRange({
  start,
  end,
  anchorDate,
  instrument,
  isPremium,
}: {
  start: Date | null
  end: Date | null
  anchorDate: Date
  instrument: Instrument
  isPremium: boolean
}) {
  const startNyTime = start ?? new Date(0)
  const startToReferenceDateNumOfDays = dateFns.differenceInCalendarDays(anchorDate, startNyTime)
  const rangeNumOfDays = dateFns.differenceInCalendarDays(end ?? anchorDate, startNyTime)

  if (rangeNumOfDays > 6 * DAYS_IN_YEAR) {
    return TIMEFRAME.m
  }

  if (rangeNumOfDays > 4 * DAYS_IN_YEAR) {
    return isPremium ? TIMEFRAME.w : TIMEFRAME.m
  }

  if (rangeNumOfDays > 1.5 * DAYS_IN_YEAR) {
    return TIMEFRAME.w
  }

  if (instrument === Instrument.Stock) {
    if (rangeNumOfDays <= 1 && startToReferenceDateNumOfDays <= 11) {
      return TIMEFRAME.i1
    }

    if (rangeNumOfDays <= 5 && startToReferenceDateNumOfDays <= 16) {
      return TIMEFRAME.i5
    }

    if (rangeNumOfDays < 8 && startToReferenceDateNumOfDays <= 200) {
      return TIMEFRAME.i30
    }
  } else {
    if (startToReferenceDateNumOfDays <= 2) {
      return TIMEFRAME.i5
    }
    if (startToReferenceDateNumOfDays <= 7) {
      return TIMEFRAME.i30
    }
  }

  return TIMEFRAME.d
}
